import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useMatch, useNavigate } from 'react-router-dom';

import type { So5FixtureEvent } from '@sorare/core/src/__generated__/globalTypes';

import { RankRow } from '../CumulativeRankRow';
import { PastLeaderboardList } from '../PastLeaderboardList';
import type { CumulativeRanking_So5UserGroupMembership } from './__generated__/index.graphql';

type Props = {
  so5Ranking: CumulativeRanking_So5UserGroupMembership;
  isMyRanking?: boolean;
  userGroupSlug: string;
  activeLineupId?: string;
  withCollapsibleRanking?: boolean;
  rankingLink?: (
    fixtureSlug: string,
    userGroupSlug: string,
    userSlug: string,
    eventType?: So5FixtureEvent
  ) => string;
};
export const CumulativeRanking = ({
  so5Ranking,
  isMyRanking,
  userGroupSlug,
  activeLineupId,
  withCollapsibleRanking,
  rankingLink,
}: Props) => {
  const navigate = useNavigate();
  const { user, lastSo5Rankings } = so5Ranking;

  const lastSo5Ranking = lastSo5Rankings?.find(Boolean);
  const lastFixture = lastSo5Ranking?.so5Fixture;

  const rankingRoute =
    rankingLink?.(
      lastFixture?.slug ?? '',
      userGroupSlug,
      user.slug,
      lastFixture?.type
    ) ?? '';

  const matchRoute = useMatch(rankingRoute);
  const isFocusedRanking = !!rankingRoute && matchRoute;

  return (
    <RankRow
      isMe={isMyRanking}
      isSelected={!!isFocusedRanking}
      ranking={so5Ranking}
      collapsible={withCollapsibleRanking}
      {...(withCollapsibleRanking
        ? {}
        : {
            onClick: () =>
              navigate(
                rankingLink?.(
                  lastFixture?.slug ?? '',
                  userGroupSlug,
                  isFocusedRanking ? '' : user.slug,
                  lastFixture?.type
                ) ?? ''
              ),
          })}
    >
      {({ expanded }) => (
        <PastLeaderboardList
          expanded={expanded}
          userSlug={user.slug}
          userGroupSlug={userGroupSlug}
          activeLineupId={activeLineupId}
        />
      )}
    </RankRow>
  );
};

CumulativeRanking.fragments = {
  So5UserGroupMembership: gql`
    fragment CumulativeRanking_So5UserGroupMembership on So5UserGroupMembership {
      id
      ranking
      score
      lastSo5Rankings(size: 1) {
        id
        so5Lineup {
          id
          ...CardsLeft_so5Lineup
        }
        so5Fixture {
          slug
          type
        }
      }
      user {
        id
        slug
      }
      ...RankRow_userRankingInterface
    }
    ${RankRow.fragments.userRankingInterface}
  ` as TypedDocumentNode<CumulativeRanking_So5UserGroupMembership>,
};
